import { css, SerializedStyles } from '@emotion/react';
import { useState } from 'react';
import { create } from 'zustand';
import { useStyles } from './TenantLogo.styles';

type TenantBranding = {
  logo_url?: string | null;
  primary_color?: string | null;
};

type TenantLogoProps = {
  fallback: React.ReactNode;
  height?: number;
  branding?: TenantBranding;
};

type TenantProps = {
  logoUrl?: string;
  primaryColor?: string;
};

type TenantStore = {
  tenant: TenantProps;
  tenantPreview: TenantProps | null;
  setTenant: (tenant: TenantProps) => void;
  setTenantPreview: (tenant: TenantProps | null) => void;
};

const initialTenant: TenantProps = {
  logoUrl: undefined,
  primaryColor: undefined,
};

const tenantStore = create<TenantStore>()((set) => ({
  tenant: initialTenant,
  tenantPreview: null,
  setTenant: (tenant: TenantProps) => set({ tenant }),
  setTenantPreview: (tenant: TenantProps | null) => set({ tenantPreview: tenant }),
}));

const useCssClass = (branding?: TenantBranding): SerializedStyles => {
  const tenant = tenantStore((s) => s.tenant);
  const tenantPreview = tenantStore((s) => s.tenantPreview);

  const primaryColor =
    branding !== undefined ? branding.primary_color ?? undefined : tenantPreview?.primaryColor ?? tenant.primaryColor;

  if (!primaryColor) {
    return css``;
  }

  return css`
    &,
    &:hover {
      background-color: transparent;
      border-color: ${primaryColor};
      color: ${primaryColor};

      svg {
        stroke: ${primaryColor};
      }
    }
  `;
};

const setTenant = (tenant: TenantProps) => {
  tenantStore.getState().setTenant(tenant);
};

const setTenantPreview = (tenant: TenantProps | null) => {
  tenantStore.getState().setTenantPreview(tenant ? { ...tenantStore.getState().tenantPreview, ...tenant } : null);
};

const getTenant = () => {
  return tenantStore.getState().tenant;
};

export const TenantLogo = ({ fallback, height = 42, branding }: TenantLogoProps) => {
  const classes = useStyles({});
  const [hasError, setHasError] = useState(false);
  const tenant = tenantStore((s) => s.tenant);
  const tenantPreview = tenantStore((s) => s.tenantPreview);

  const logoUrl = branding !== undefined ? branding.logo_url ?? undefined : tenantPreview?.logoUrl ?? tenant.logoUrl;

  if (logoUrl && !hasError) {
    return <img src={logoUrl} alt="Logo" height={height} css={classes.root} onError={() => setHasError(true)} />;
  }

  return fallback;
};

TenantLogo.useColorsCss = useCssClass;
TenantLogo.setTenant = setTenant;
TenantLogo.setTenantPreview = setTenantPreview;
TenantLogo.getTenant = getTenant;
